import { Injectable, computed, inject, signal } from '@angular/core';
import { APP_CONFIG } from '@cardapio-online/config';
import type { NotificationEvent } from '../models/notification-event.model';
import type { NotificationConnectionState } from '../models/notification.types';

@Injectable({
  providedIn: 'root',
})
export class NotificationStore {
  private static readonly STORAGE_VERSION = 1;
  private static readonly HISTORY_LIMIT = 30;

  private readonly cfg = inject(APP_CONFIG);

  private readonly eventsSignal = signal<NotificationEvent[]>(
    this.readStoredEvents()
  );
  private readonly connectionStateSignal =
    signal<NotificationConnectionState>('idle');
  private readonly lastEventAtSignal = signal<string | null>(
    this.eventsSignal()[0]?.createdAt ?? null
  );

  readonly events = computed(() => this.eventsSignal());
  readonly unreadCount = computed(
    () => this.eventsSignal().filter((item) => !item.readAt).length
  );
  readonly hasUnread = computed(() => this.unreadCount() > 0);
  readonly connectionState = computed(() => this.connectionStateSignal());
  readonly lastEventAt = computed(() => this.lastEventAtSignal());

  add(event: NotificationEvent): NotificationEvent {
    const next = this.normalizeEvent(event);

    this.eventsSignal.update((current) =>
      [next, ...current.filter((item) => item.id !== next.id)].slice(
        0,
        NotificationStore.HISTORY_LIMIT
      )
    );
    this.lastEventAtSignal.set(next.createdAt);
    this.persist();

    return next;
  }

  markAsRead(id: string): void {
    let changed = false;

    this.eventsSignal.update((current) =>
      current.map((item) => {
        if (item.id !== id || item.readAt) {
          return item;
        }

        changed = true;
        return {
          ...item,
          readAt: new Date().toISOString(),
        };
      })
    );

    if (changed) {
      this.persist();
    }
  }

  markAllAsRead(): void {
    const now = new Date().toISOString();

    this.eventsSignal.update((current) =>
      current.map((item) =>
        item.readAt
          ? item
          : {
              ...item,
              readAt: now,
            }
      )
    );
    this.persist();
  }

  remove(id: string): void {
    const current = this.eventsSignal();
    const next = current.filter((item) => item.id !== id);

    if (next.length === current.length) {
      return;
    }

    this.eventsSignal.set(next);
    this.persist();
  }

  clear(): void {
    this.eventsSignal.set([]);
    this.persist();
  }

  latest(limit = 5): NotificationEvent[] {
    return this.eventsSignal().slice(0, Math.max(1, limit));
  }

  setConnectionState(state: NotificationConnectionState): void {
    this.connectionStateSignal.set(state);
  }

  setLastEventAt(value: string | null): void {
    this.lastEventAtSignal.set(value);
  }

  private persist(): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(
      this.getStorageKey(),
      JSON.stringify({
        version: NotificationStore.STORAGE_VERSION,
        events: this.eventsSignal(),
      })
    );
  }

  private readStoredEvents(): NotificationEvent[] {
    if (typeof window === 'undefined') {
      return [];
    }

    const raw = localStorage.getItem(this.getStorageKey());

    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as {
        version?: number;
        events?: NotificationEvent[];
      };

      if (parsed.version !== NotificationStore.STORAGE_VERSION) {
        return [];
      }

      return Array.isArray(parsed.events)
        ? parsed.events.map((event) => this.normalizeEvent(event))
        : [];
    } catch {
      return [];
    }
  }

  private normalizeEvent(event: NotificationEvent): NotificationEvent {
    return {
      ...event,
      title: event.title.trim(),
      description: event.description.trim(),
      imageUrl: event.imageUrl?.trim() || null,
      route: event.route?.trim() || null,
      orderId: event.orderId?.trim() || null,
      readAt: event.readAt?.trim() || null,
    };
  }

  private getStorageKey(): string {
    return `menuhub:${this.resolveScope()}:admin:notifications`;
  }

  private resolveScope(): string {
    const configuredTenant = this.cfg.tenantSlug?.trim().toLowerCase();

    if (configuredTenant) {
      return configuredTenant;
    }

    if (typeof window === 'undefined') {
      return 'server';
    }

    return window.location.hostname.trim().toLowerCase() || 'default';
  }
}
